import { useEffect, useRef, useState } from 'react';
import { ALPHABET } from '@alphabet-hunter/shared';
import type { GameView, WordFeedback } from '../types';
import { AppScreen } from './AppScreen';

interface GameScreenProps {
  game: GameView | null;
  feedback: WordFeedback | null;
  onSubmitWord: (word: string) => void;
  onLeave: () => void;
}

function letterClass(letter: string, game: GameView | null): string {
  if (game?.foundLetters.includes(letter)) {
    return 'bg-atlas-pine dark:bg-atlas-pine-dark text-white';
  }
  if (game && letter === game.currentLetter) {
    return 'bg-atlas-brass dark:bg-atlas-brass-dark text-atlas-ink animate-pulse-chip';
  }
  return 'bg-atlas-surface dark:bg-atlas-surface-dark border border-atlas-rule dark:border-atlas-rule-dark text-atlas-ink-soft dark:text-atlas-ink-soft-dark';
}

const feedbackClass: Record<WordFeedback['type'], string> = {
  success: 'bg-atlas-pine/10 dark:bg-atlas-pine-dark/15 text-atlas-pine dark:text-atlas-pine-dark',
  error: 'bg-atlas-brick/10 dark:bg-atlas-brick-dark/15 text-atlas-brick dark:text-atlas-brick-dark',
  info: 'bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink-soft dark:text-atlas-ink-soft-dark',
};

export function GameScreen({ game, feedback, onSubmitWord, onLeave }: GameScreenProps) {
  const [word, setWord] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const currentLetter = game?.currentLetter ?? null;
  const letterElRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = letterElRef.current;
    if (!el || !currentLetter) return;
    el.classList.remove('animate-letter-in');
    void el.offsetHeight; // reflow to restart the animation
    el.classList.add('animate-letter-in');
  }, [currentLetter]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [currentLetter]);

  const sortedPlayers = [...(game?.players ?? [])].sort((a, b) => b.score - a.score);

  function submit() {
    if (!word.trim()) return;
    onSubmitWord(word);
    setWord('');
    inputRef.current?.focus();
  }

  return (
    <AppScreen>
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0">
        <div className="flex items-center justify-between pt-[calc(env(safe-area-inset-top)+14px)] pb-1.5 px-4">
          <button
            onClick={onLeave}
            aria-label="Leave game"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-atlas-ink dark:text-atlas-ink-dark"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className="text-xs font-semibold uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Game in Progress
          </span>
          <div className="w-[44px]" />
        </div>

        <div className="px-4 pb-3">
          <div className="bg-atlas-surface dark:bg-atlas-surface-dark rounded-2xl p-2.5 shadow-sm">
            <div className="grid grid-cols-[repeat(13,1fr)] grid-rows-2 gap-1">
              {ALPHABET.map((letter) => (
                <div
                  key={letter}
                  className={`aspect-square flex items-center justify-center rounded text-[10px] sm:text-sm font-bold transition-colors ${letterClass(letter, game)}`}
                >
                  {letter}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-2.5 justify-center">
            {sortedPlayers.map((p, i) => (
              <div
                key={`${p.name}-${i}`}
                className="flex items-center gap-1.5 rounded-full border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark px-3 py-1"
              >
                <span className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark max-w-[100px] truncate">
                  {p.name}
                </span>
                <span className="font-mono font-bold text-sm text-atlas-brass dark:text-atlas-brass-dark">{p.score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 gap-5">
        <div className="text-center flex flex-col items-center gap-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Hunt for...
          </p>
          <div
            ref={letterElRef}
            className="font-serif text-[clamp(100px,28vw,180px)] font-bold leading-none text-atlas-brass dark:text-atlas-brass-dark"
          >
            {currentLetter ?? ''}
          </div>
          <p className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark min-h-[1.2em]">
            {game?.nextLetter ? `Next: ${game.nextLetter}` : 'Last letter!'}
          </p>
        </div>

        <div className="w-full max-w-[380px] flex flex-col gap-2.5">
          <input
            type="text"
            ref={inputRef}
            value={word}
            onChange={(e) => setWord(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            placeholder="Type a word starting with this letter…"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={40}
            className="w-full px-4 py-4 rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink dark:text-atlas-ink-dark placeholder-atlas-ink-soft/60 dark:placeholder-atlas-ink-soft-dark/60 text-[17px] focus:outline-none focus:ring-2 focus:ring-atlas-brass dark:focus:ring-atlas-brass-dark"
          />
          <button
            onClick={submit}
            className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all"
          >
            Submit
          </button>
        </div>

        {feedback && (
          <div className={`w-full max-w-[380px] px-4 py-3.5 rounded-xl text-center text-sm font-semibold animate-fade-in ${feedbackClass[feedback.type]}`}>
            {feedback.message}
          </div>
        )}
      </div>
    </AppScreen>
  );
}
